/**
 * COMPANY AND DIVISION UTILITY FUNCTIONS
 * Handles normalization for Firebase keys, display names, and URL paths
 */

// Master list of all companies and their divisions
const COMPANY_DATA = {};

// Default companies that cannot be deleted
export const DEFAULT_COMPANIES = Object.keys(COMPANY_DATA);

// All companies list for dropdowns
export const ALL_COMPANIES = [...DEFAULT_COMPANIES];

/**
 * Normalizes any company name variant to standard display name
 * @param {string} name - Input company name
 * @returns {string} Normalized display name
 */
export const normalizeCompanyKey = (name = '') => {
  if (!name) return '';

  const lowerName = name.trim().toLowerCase();
  
  // Check exact matches first
  for (const [company, data] of Object.entries(COMPANY_DATA)) {
    if (company.toLowerCase() === lowerName) return company;
    if (data.aliases.includes(lowerName)) return company;
  }

  // Fallback to title case
  return toTitleCase(name);
};

/**
 * Normalizes division names to standard format
 * @param {string} division - Input division name
 * @param {string} company - Company name (optional)
 * @returns {string} Normalized division name
 */
export const normalizeDivisionKey = (division = '', company = '') => {
  if (!division) return '';
  
  const normalizedDivision = toTitleCase(division.trim());
  
  // If company is provided, verify division exists for that company
  if (company) {
    const normalizedCompany = normalizeCompanyKey(company);
    const companyDivisions = COMPANY_DATA[normalizedCompany]?.divisions || [];
    
    if (companyDivisions.includes(normalizedDivision)) {
      return normalizedDivision;
    }
  }
  
  return normalizedDivision;
};

/**
 * Gets Firebase-safe key for company (preserves spaces)
 * @param {string} displayName - Company display name
 * @returns {string} Firebase-safe key
 */
export const firebaseCompanyKey = (displayName = '') => {
  return displayName.trim(); // Firebase allows spaces in keys
};

/**
 * Gets Firebase-safe key for division (with underscore replacement)
 * @param {string} division - Division name
 * @returns {string} Firebase-safe key
 */
export const firebaseDivisionKey = (division = '') => {
  return division.trim().replace(/\s+/g, '_');
};

/**
 * Creates URL/path-safe version of names
 * @param {string} value - Input string
 * @returns {string} Path-safe version
 */
export const normalizeForPath = (value = '') => {
  return value
    .replace(/[.#$/[\]]/g, '') // Remove problematic chars
    .replace(/\s+/g, '_')      // Spaces to underscores
    .toLowerCase();
};

/**
 * Gets all divisions for a company
 * @param {string} company - Company name
 * @returns {string[]} Array of divisions
 */
export const getCompanyDivisions = (company = '') => {
  const normalized = normalizeCompanyKey(company);
  return COMPANY_DATA[normalized]?.divisions || [];
};

/**
 * Generates test report key for Firebase
 * @param {string} companyName - Company name
 * @param {string|number} kva - Transformer kVA rating
 * @returns {string} Test report key
 */
export const getTestReportKey = (companyName, kva) => {
  const normalized = normalizeForPath(companyName).toUpperCase();
  return `${normalized}_${kva}`;
};

// Helper function for title case conversion
const toTitleCase = (str = '') => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Checks if a division belongs to a company
 * @param {string} company - Company name
 * @param {string} division - Division name
 * @returns {boolean}
 */
export const isDivisionValid = (company, division) => {
  return true;
};